import { isEqual } from 'lodash';
import { isEmptyStoreysGeometry } from '../design/storey.helpers';
import type { Storey } from '../../models/design/storey.model';
import type { ProjectEventActivity } from '../../models/project-event.model';

/** Why the map committed storeys, when it is more than a footprint edit. */
export type StoreysCommitReason = 'building-move' | 'storey-height';

/**
 * History entries for one map commit of a building's storeys: its first
 * footprint creates it, a building move is one entry, otherwise one entry for
 * the storeys whose footprint changed, or whose height was explicitly edited.
 */
export const designCommitEvents = ({
  buildingId,
  previous,
  next,
  reason,
}: {
  buildingId: string;
  previous: Storey[];
  next: Storey[];
  reason?: StoreysCommitReason;
}): ProjectEventActivity[] => {
  const editedStoreyIds = next.flatMap((storey) => {
    const before = previous.find(({ id }) => id === storey.id);
    if (!before) return [];
    const changed =
      reason === 'storey-height'
        ? before.height !== storey.height
        : !isEqual(before.shapes, storey.shapes);
    if (!changed) return [];
    return [storey.id];
  });
  if (isEmptyStoreysGeometry(previous)) {
    return isEmptyStoreysGeometry(next)
      ? []
      : [{ kind: 'building-created', buildingId }];
  }
  const [storeyId, ...otherStoreyIds] = editedStoreyIds;
  if (!storeyId) return [];
  if (reason === 'building-move')
    return [{ kind: 'building-moved', buildingId }];
  return [
    {
      kind: 'storey-footprint-edited',
      buildingId,
      storeyId,
      ...(otherStoreyIds.length ? { storeyIds: editedStoreyIds } : {}),
    },
  ];
};
